import { datadogRum, type RumEvent } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';
import type { User } from '@contexts/AuthContextStore';
import { getFirstVisitUtm, getSessionUtm } from '@utils/utm';

const APPLICATION_ID = 'ea749453-483e-465d-b587-9e7d151e32ad';
const CLIENT_TOKEN = 'pubda27331ee6ff386186b8bd225212b409';
const PROXY_URL = 'https://fe-events.ai-build.com';

// Thrown by wallet / webview scripts the browser injects into the page, never by our bundle.
const INJECTED_SCRIPT_ERROR_PATTERNS: readonly string[] = [
  'window.ethereum',
  'No injected ethereum object',
  'window.webkit.messageHandlers',
];

// RUM/Logs ship to the commercial Datadog proxy (PROXY_URL). GovCloud is
// network-isolated and must never reach it, so the SDKs stay fully off there.
export function isDatadogEnabled(): boolean {
  if (import.meta.env.DEV) return false;
  const env = import.meta.env.VITE_ENV || 'production';
  return !env.startsWith('gov');
}

/**
 * Decide whether the RUM SDK should propagate distributed-tracing headers
 * to a given outbound request URL. We propagate only to our own backend
 * API (path starts with {@code /api/}) so trace context reaches services
 * that can correlate it, and skip everything else.
 *
 * Exported for unit tests; not part of the public surface.
 */
export function shouldPropagateTracing(url: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(url, globalThis.location?.origin ?? 'http://localhost');
  } catch {
    return false;
  }
  return parsed.pathname.startsWith('/api/');
}

export function isInjectedScriptError(message: string | undefined): boolean {
  if (!message) return false;
  return INJECTED_SCRIPT_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
}

const SHARED_APP_TOKEN = /(\/apps\/shared\/)[^/?#]+/g;

/** A public link's token is a capability; it never leaves the browser in telemetry. */
export const redactSharedAppTokens = (url: string): string => url.replace(SHARED_APP_TOKEN, '$1redacted');

export function filterRumEvent(event: RumEvent): boolean {
  event.view.url = redactSharedAppTokens(event.view.url);
  if (event.view.referrer) event.view.referrer = redactSharedAppTokens(event.view.referrer);
  if (event.type === 'resource') event.resource.url = redactSharedAppTokens(event.resource.url);
  return !(event.type === 'error' && isInjectedScriptError(event.error.message));
}

const environmentMappings: Record<string, string> = {
  test: 'test',
  production: 'prod',
};

// Email domains considered internal to Aibuild.
const INTERNAL_EMAIL_DOMAINS = ['@ai-build.com', '@ai-build.io'];

function isInternalEmail(email: string): boolean {
  const lower = email.toLowerCase();
  return INTERNAL_EMAIL_DOMAINS.some((d) => lower.includes(d));
}

/**
 * Map OrgRole to the Datadog tag value expected by the monitoring dashboards.
 * Mirrors the mapping in ObservabilityInterceptor.java.
 */
function mapOrgRole(orgRole?: string): string | undefined {
  switch (orgRole) {
    case 'AIBUILD_ADMIN': return 'aibuild_admin';
    case 'ORG_ADMIN': return 'org_admin';
    case 'MEMBER': return 'member';
    default: return undefined;
  }
}

/**
 * Map backend planType to the canonical billing_plan tag values used in Datadog.
 * Matches the values documented in AIB-6466.
 */
function mapBillingPlan(planType?: string | null): string {
  if (!planType) return 'none';
  switch (planType.toUpperCase()) {
    case 'NONE': return 'none';
    case 'MIGRATED_FREE': return 'free_cam';
    case 'FREE': return 'free_new';
    case 'STARTER': return 'starter';
    case 'TEAM': return 'team';
    case 'ENTERPRISE': return 'enterprise';
    case 'BENCHMARK': return 'enterprise';
    default: return planType.toLowerCase();
  }
}

export const initDatadog = () => {
  // Off in local dev and in GovCloud (network-isolated, no commercial proxy).
  if (!isDatadogEnabled()) {
    return;
  }

  const env = import.meta.env.VITE_ENV || 'production';
  const version = import.meta.env.VITE_VERSION || 'unknown';

  if (datadogRum.getInitConfiguration() === undefined) {
    datadogRum.init({
      applicationId: APPLICATION_ID,
      clientToken: CLIENT_TOKEN,
      site: 'datadoghq.eu',
      service: 'aibuild-os-frontend',
      env: environmentMappings[env] || env,
      version,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'allow',
      startSessionReplayRecordingManually: true,
      enableExperimentalFeatures: ['feature_flags'],
      proxy: PROXY_URL,
      // Propagate trace context into backend API requests so RUM sessions are
      // correlated with backend APM traces in Datadog. Match on the parsed
      // {@code URL.pathname} rather than a substring of the raw URL -- see
      // {@link shouldPropagateTracing}.
      allowedTracingUrls: [
        {
          match: shouldPropagateTracing,
          propagatorTypes: ['datadog', 'tracecontext'],
        },
      ],
      beforeSend: filterRumEvent,
    });
  }

  if (datadogLogs.getInitConfiguration() === undefined) {
    datadogLogs.init({
      clientToken: CLIENT_TOKEN,
      site: 'datadoghq.eu',
      // Route Logs intake through the same first-party proxy as RUM so
      // ad-blockers don't tank our error reporting and both SDKs share a
      // single well-known origin.
      proxy: PROXY_URL,
      forwardErrorsToLogs: true,
      sessionSampleRate: 100,
      forwardConsoleLogs: 'all',
    });
  }
};

/**
 * Map onboarding profile answers to flat `profile_*` RUM attributes.
 * Lists are joined with commas — RUM tag values must be scalar so we can
 * facet on them in the explorer (substring matching still works).
 *
 * Mirrors `build_profile_tags` in
 * `python-ai-service/ai_service/observability/profile_tags.py` so RUM and
 * LLM Obs surface the same `profile_*` keys for cross-cutting analysis.
 * Keep the two key lists in sync when adding new onboarding fields.
 */
const PROFILE_SCALAR_KEYS = [
  'companyName',
  'role',
  'industry',
  'companySize',
  'mfgSetupOther',
  'intentsOther',
  'cadOther',
  'caeOther',
  'plmOther',
  'camOther',
  'mesOther',
] as const;

const PROFILE_LIST_KEYS = [
  'intents',
  'cadSoftware',
  'caeTools',
  'plm',
  'camSoftware',
  'mfgSetup',
  'mes',
] as const;

/**
 * Convert a camelCase profile key to snake_case so RUM tags read naturally
 * (`profile_company_name` rather than `profile_companyName`). Mirrors the
 * `_snake` helper on the Python side.
 */
function camelToSnake(key: string): string {
  return key.replaceAll(/[A-Z]/g, (ch) => `_${ch.toLowerCase()}`);
}

export function buildProfileRumAttributes(
  profile: User['profileData'],
): Record<string, string> {
  const out: Record<string, string> = {};
  if (!profile) return out;
  for (const key of PROFILE_SCALAR_KEYS) {
    const value = profile[key];
    if (typeof value === 'string' && value.trim().length > 0) {
      out[`profile_${camelToSnake(key)}`] = value;
    }
  }
  for (const key of PROFILE_LIST_KEYS) {
    const value = profile[key];
    if (Array.isArray(value) && value.length > 0) {
      out[`profile_${camelToSnake(key)}`] = value.join(',');
    }
  }
  return out;
}

/**
 * Set the full Datadog user + org context from the authenticated User object.
 * Sets usr.* tags on the RUM user and org.* tags as global context properties
 * so every subsequent RUM event carries the complete monitoring context.
 *
 * Called on login, MFA completion, SSO init, page-refresh restore, and token refresh.
 */
export const setDatadogUser = (user: User) => {
  if (!isDatadogEnabled()) return;

  const billingPlan = mapBillingPlan(user.planType);
  const isInternal = isInternalEmail(user.email);
  const role = mapOrgRole(user.orgRole);

  const firstUtm = getFirstVisitUtm();

  datadogRum.setUser({
    id: user.id,
    name: user.displayName ?? undefined,
    email: user.email,
    org_name: user.orgName ?? undefined,
    billing_plan: billingPlan,
    has_cam_access: String(user.hasCamAccess ?? false),
    role,
    is_internal: String(isInternal),
    signup_date: user.userCreatedAt ?? undefined,
    last_login: user.lastLoginAt ?? undefined,
    activation_date: user.userActivatedAt ?? undefined,
    ...(user.groups?.length && { groups: user.groups.map((g) => g.name).join(',') }),
    ...(firstUtm?.source && { first_utm_source: firstUtm.source }),
    ...(firstUtm?.medium && { first_utm_medium: firstUtm.medium }),
    ...(firstUtm?.campaign && { first_utm_campaign: firstUtm.campaign }),
    // Lead-qualification (post-payment onboarding) attributes — flat profile_*
    // keys so they're filterable in RUM Explorer / Session Replay segmentation.
    ...buildProfileRumAttributes(user.profileData),
  });

  // Session-level UTM context — set when the user arrived via a marketing
  // link so every RUM event in this session carries the re-engagement source.
  const sessionUtm = getSessionUtm();
  if (sessionUtm) {
    datadogRum.setGlobalContextProperty('utm', sessionUtm);
  } else {
    datadogRum.removeGlobalContextProperty('utm');
  }

  // Org-level global context — tagged on every RUM event automatically.
  // Explicitly clear when orgId is absent so stale context from a previous
  // setDatadogUser call doesn't leak onto events for org-less users.
  if (user.orgId) {
    datadogRum.setGlobalContextProperty('org', {
      id: user.orgId,
      name: user.orgName ?? undefined,
      status: (user.billingStatus ?? 'PENDING').toLowerCase(),
      billing_plan: billingPlan,
      is_internal: isInternal,
      created_date: user.orgCreatedAt ?? undefined,
      user_count: user.orgUserCount ?? undefined,
      credits_per_month: user.orgCreditsPerMonth ?? undefined,
      next_billing_date: user.orgNextBillingDate ?? undefined,
    });
  } else {
    datadogRum.removeGlobalContextProperty('org');
  }

  datadogRum.startSessionReplayRecording();
};

export const clearDatadogUser = () => {
  if (!isDatadogEnabled()) return;

  datadogRum.clearUser();
  datadogRum.removeGlobalContextProperty('org');
  datadogRum.removeGlobalContextProperty('utm');
};

export const trackAction = (name: string, context?: Record<string, unknown>) => {
  if (!isDatadogEnabled()) return;
  datadogRum.addAction(name, context);
};

export const trackError = (error: Error, context?: Record<string, unknown>) => {
  if (!isDatadogEnabled()) return;
  datadogRum.addError(error, context);
};

/**
 * Track an onboarding funnel event.
 * Each event carries usr.id, org.id, usr.billing_plan, usr.has_cam_access
 * from the current RUM user context automatically.
 *
 * Event names match AIB-6466 specification:
 *   os_account_created | os_first_prompt | os_first_output |
 *   os_second_session  | os_first_payment
 */
export type FunnelEvent =
  | 'os_account_created'
  | 'os_first_prompt'
  | 'os_first_output'
  | 'os_second_session'
  | 'os_first_payment'
  // Processes A/B (AIB-7495): nodes added, test runs started and publishes are
  // the outcome metrics; the assistant event carries the gate exposure so a
  // cohort can be split on it.
  | 'process_node_added'
  | 'process_test_run_started'
  | 'process_published'
  | 'process_assistant_message_sent';

export const trackFunnelEvent = (
  event: FunnelEvent,
  context?: Record<string, unknown>,
) => {
  if (!isDatadogEnabled()) return;
  datadogRum.addAction(event, context);
};

/**
 * Stamp a feature gate's value onto every subsequent RUM event, so a funnel
 * can be split by cohort without each call site having to know the gate.
 */
export const setFeatureGateContext = (gate: string, enabled: boolean) => {
  if (!isDatadogEnabled()) return;
  datadogRum.setGlobalContextProperty(`feature_gate.${gate}`, enabled);
};
